import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/api";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format(value || 0);
};

const getLogoBase64 = async () => {
  try {
    const response = await fetch("/img/icons/android-chrome-512x512.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error("Could not load logo", e);
    return null;
  }
};

async function fetchImageAsBase64(url) {
  if (!url) return null;
  if (url.startsWith("data:")) return url;

  try {
    // Try fetching directly first (works for same-origin or CORS-enabled)
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log("Direct fetch failed, trying backend proxy...", e);
    try {
      const result = await api.fetchImage({ url });
      return `data:${result.data.mimeType};base64,${result.data.base64}`;
    } catch (backendError) {
      console.error("Backend fetch failed", backendError);
      return null;
    }
  }
}

export const generateMuonitusPdf = async (
  cateringEvent,
  checklistItems,
  formattedDate,
  timePickerTime,
  timePickerEndTime,
  selectedMenuDescription,
  typeName,
  costAllocations,
  formattedEndDate
) => {
  const doc = new jsPDF();
  const logoBase64 = await getLogoBase64();
  const pageWidth = doc.internal.pageSize.width;

  // Header
  if (logoBase64) {
    doc.addImage(logoBase64, "PNG", 15, 10, 25, 25);
  }

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Noormarkun VPK ry", 195, 15, { align: "right" });
  doc.text("Palotie 4", 195, 20, { align: "right" });
  doc.text("29600 Noormarkku", 195, 25, { align: "right" });

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(cateringEvent.description || "Muonitus", 105, 35, {
    align: "center",
  });

  let yPos = 50;

  // --- SECTION 1: SUUNNITTELU (Planning) ---
  // We will group: Ajankohta, Perustiedot, Muonitus, Henkilöstö

  const planningBody = [];

  // 1.1 Ajankohta ja Perustiedot
  planningBody.push([
    {
      content: "Ajankohta ja Perustiedot",
      colSpan: 2,
      styles: { fontStyle: "bold", fillColor: [220, 220, 220] },
    },
  ]);

  let timeString = "";
  if (formattedEndDate && formattedEndDate !== formattedDate) {
    timeString = `${timePickerTime || ""} - ${formattedEndDate} ${
      timePickerEndTime || ""
    }`;
  } else {
    timeString = timePickerEndTime
      ? `${timePickerTime || ""} - ${timePickerEndTime}`
      : timePickerTime || "";
  }

  planningBody.push(["Ajankohta", `${formattedDate} ${timeString}`]);
  planningBody.push(["Tyyppi", typeName || "-"]);
  planningBody.push(["Kohde/Osoite", cateringEvent.siteId || "-"]);
  planningBody.push(["Tilaaja / Maksaja", cateringEvent.payer || "-"]);
  if (cateringEvent.payer === "Pelastuslaitos") {
    planningBody.push([
      "Tärkeät numerot",
      "P511: 044 098 3181\nTilannekeskus: 02 621 1500\nRSAP31: 044 701 1503\nRSAP32: 044 701 7353\nRSAP33: 044 701 7462",
    ]);
  }
  planningBody.push(["Yhteyshenkilö", cateringEvent.subscriberContact || "-"]);
  if (cateringEvent.subscriberPhone) {
    planningBody.push(["Puhelin", cateringEvent.subscriberPhone]);
  }
  planningBody.push(["Tilausviite", cateringEvent.orderReference || "-"]);

  // 1.2 Muonitus
  planningBody.push([
    {
      content: "Muonitus",
      colSpan: 2,
      styles: { fontStyle: "bold", fillColor: [220, 220, 220] },
    },
  ]);
  planningBody.push(["Menu", cateringEvent.contentDescription || "-"]);
  if (selectedMenuDescription) {
    planningBody.push(["Sisältö", selectedMenuDescription]);
  }
  planningBody.push([
    "Tilattu hlö-määrä",
    cateringEvent.headCountEstimate || 0,
  ]);
  if (cateringEvent.diets) {
    planningBody.push(["Ruokavaliot", cateringEvent.diets]);
  }

  // 1.3 Henkilöstö
  planningBody.push([
    {
      content: "Henkilöstö",
      colSpan: 2,
      styles: { fontStyle: "bold", fillColor: [220, 220, 220] },
    },
  ]);
  planningBody.push([
    "Keittiöryhmän johtaja",
    cateringEvent.kitchenLeader || "-",
  ]);
  planningBody.push(["Kenttäryhmän johtaja", cateringEvent.fieldLeader || "-"]);
  if (cateringEvent.groupMembers && cateringEvent.groupMembers.length > 0) {
    planningBody.push(["Muonitusryhmä", cateringEvent.groupMembers.join(", ")]);
  }

  autoTable(doc, {
    startY: yPos,
    body: planningBody,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 4, lineColor: [200, 200, 200] },
    columnStyles: {
      0: {
        fontStyle: "bold",
        cellWidth: 60,
        fillColor: [245, 245, 245],
        textColor: [50, 50, 50],
      },
      1: { cellWidth: "auto" },
    },
  });

  // Checklist (Equipment)
  doc.addPage();
  yPos = 20;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Varustelista", 14, yPos);
  yPos += 6;

  // Filter to only needed items
  const neededChecklistItems = checklistItems.filter(
    (item) => cateringEvent.checklist && cateringEvent.checklist[item.label]
  );

  if (neededChecklistItems.length > 0) {
    const checklistData = neededChecklistItems.map((item) => [
      "", // Manual Checkbox
      {
        content: item.label,
        styles: {
          cellPadding: {
            top: 3,
            right: 3,
            bottom: 3,
            left: item.indent ? 15 : 3,
          },
        },
      },
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [["Pakattu", "Varuste"]],
      body: checklistData,
      theme: "grid", // Adds borders
      headStyles: { fillColor: [220, 220, 220], textColor: 0 },
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: 160 },
      },
      didDrawCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          // Draw checkbox
          doc.rect(data.cell.x + 8, data.cell.y + 2, 4, 4);

          // If PACKED, draw a checkmark
          const item = neededChecklistItems[data.row.index];
          if (
            item &&
            cateringEvent.packedItems &&
            cateringEvent.packedItems[item.label]
          ) {
            doc.setFontSize(12);
            doc.text("X", data.cell.x + 8.5, data.cell.y + 5.5);
          }
        }
      },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.text("Ei valittuja varusteita.", 14, yPos + 5);
    yPos += 15;
  }

  // Shopping List on new page if needed, or continue if space
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  // Header again for new page? Or just title.
  // Let's just put the title "Kauppalista"
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Kauppalista", 14, yPos);
  yPos += 10;

  // Group by category
  const groupedShoppingList = {};
  (cateringEvent.shoppingList || []).forEach((item) => {
    const cat = item.category || "Muu";
    if (!groupedShoppingList[cat]) groupedShoppingList[cat] = [];
    groupedShoppingList[cat].push(item);
  });

  const shoppingBody = [];
  Object.keys(groupedShoppingList)
    .sort()
    .forEach((cat) => {
      // Add category header row
      shoppingBody.push([
        {
          content: cat,
          colSpan: 4,
          styles: { fillColor: [220, 220, 220], fontStyle: "bold" },
        },
      ]);
      groupedShoppingList[cat].forEach((item) => {
        shoppingBody.push([
          item.name,
          `${item.amount} ${item.unit}`,
          item.collected ? "X" : "",
          item.comment || "",
        ]);
      });
    });

  autoTable(doc, {
    startY: yPos,
    head: [["Tuote", "Määrä", "Kerätty", "Huom"]],
    body: shoppingBody,
    theme: "grid",
    headStyles: { fillColor: [220, 220, 220], textColor: 0 },
    styles: { fontSize: 10 },
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Public Notes
  if (cateringEvent.publicNotes) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Kauppalistan muistiinpanot", 14, yPos);
    yPos += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const splitNotes = doc.splitTextToSize(
      cateringEvent.publicNotes,
      pageWidth - 28
    );
    doc.text(splitNotes, 14, yPos);
    yPos += splitNotes.length * 5 + 10;
  }

  // Talous (Financials)
  const hasSalesData =
    (cateringEvent.headCountReal || 0) > 0 ||
    (cateringEvent.unitPrice || 0) > 0;
  const hasPurchaseData = (cateringEvent.receiptTotal || 0) > 0;

  if (hasSalesData || hasPurchaseData) {
    doc.addPage();
    yPos = 20;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Talous", 14, yPos);
    yPos += 10;

    const financialBody = [];
    if (hasSalesData) {
      const total =
        (cateringEvent.headCountEstimate || 0) * (cateringEvent.unitPrice || 0);
      financialBody.push([
        {
          content: "Myyntilaskutus",
          colSpan: 2,
          styles: { fontStyle: "bold", fillColor: [240, 240, 240] },
        },
      ]);
      financialBody.push([
        "Tilattu hlö-määrä",
        cateringEvent.headCountEstimate || 0,
      ]);
      financialBody.push([
        "Yksikköhinta",
        formatCurrency(cateringEvent.unitPrice),
      ]);
      financialBody.push(["Yhteensä", formatCurrency(total)]);
    }

    if (hasPurchaseData) {
      financialBody.push([
        {
          content: "Ostolaskutus (Kuitit)",
          colSpan: 2,
          styles: { fontStyle: "bold", fillColor: [240, 240, 240] },
        },
      ]);
      financialBody.push([
        "Kauppa / Hankintapaikka",
        cateringEvent.purchaseLocation || "-",
      ]);
      financialBody.push([
        "Kuittien summa",
        formatCurrency(cateringEvent.receiptTotal),
      ]);
    }

    if (costAllocations && costAllocations.length > 0) {
      financialBody.push([
        {
          content: "Kustannusten kohdistus",
          colSpan: 2,
          styles: { fontStyle: "bold", fillColor: [240, 240, 240] },
        },
      ]);
      financialBody.push(["Kustannuspaikat", costAllocations.join(", ")]);
    }

    autoTable(doc, {
      startY: yPos,
      body: financialBody,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 4, lineColor: [200, 200, 200] },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: "auto" },
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // --- SECTION 4: Palaute ja Logi ---
  const feedbackBody = [];
  feedbackBody.push([
    {
      content: "Palaute ja Logi",
      colSpan: 2,
      styles: { fontStyle: "bold", fillColor: [220, 220, 220] },
    },
  ]);
  feedbackBody.push(["Palaute", cateringEvent.feedback || "-"]);
  feedbackBody.push([
    "Toteutunut hlö-määrä",
    cateringEvent.headCountReal || "-",
  ]);
  feedbackBody.push([
    "Osallistujatiedot",
    cateringEvent.participantsInfo || "-",
  ]);

  autoTable(doc, {
    startY: yPos,
    body: feedbackBody,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 4, lineColor: [200, 200, 200] },
    columnStyles: {
      0: {
        fontStyle: "bold",
        cellWidth: 60,
        fillColor: [245, 245, 245],
        textColor: [50, 50, 50],
      },
      1: { cellWidth: "auto" },
    },
  });

  // Receipts Images
  const receiptUrls = cateringEvent.receiptImageUrls || [];
  // Backward compatibility
  if (receiptUrls.length === 0 && cateringEvent.receiptImageUrl) {
    receiptUrls.push(cateringEvent.receiptImageUrl);
  }

  if (receiptUrls.length > 0) {
    for (const url of receiptUrls) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text("Kuitti", 14, 20);

      try {
        const base64data = await fetchImageAsBase64(url);
        if (base64data) {
          const props = doc.getImageProperties(base64data);
          const pageHeight = doc.internal.pageSize.getHeight();
          const maxWidth = doc.internal.pageSize.getWidth() - 30;
          const maxHeight = pageHeight - 40; // 30 top margin, 10 bottom

          let pdfWidth = maxWidth;
          let pdfHeight = (props.height * pdfWidth) / props.width;

          // If image is too tall, scale by height
          if (pdfHeight > maxHeight) {
            pdfHeight = maxHeight;
            pdfWidth = (props.width * pdfHeight) / props.height;
          }

          doc.addImage(base64data, "JPEG", 15, 30, pdfWidth, pdfHeight);
        } else {
          doc.text("Kuvan lataaminen epäonnistui", 15, 30);
        }
      } catch (e) {
        console.error("Failed to add receipt image", e);
        doc.text("Virhe kuvan latauksessa", 15, 30);
      }
    }
  }

  doc.save(`muonitus_${formattedDate}.pdf`);
};
